import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import SocialDistanceIcon from '@mui/icons-material/SocialDistance';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import type { FC } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { formatTooltipWithShortcut } from 'helpers/formatTooltipWithShortcut';
import { CloseIcon, SearchIcon } from 'icons';
import { TicketsFiltersBar } from 'pages/TicketsPage/components/TicketsFiltersBar/TicketsFiltersBar';
import type { ChangeTicketsListFormValues } from 'pages/TicketsPage/hooks/useTicketsPage';

import ViewScheduleFilesPopup from '../TicketDetailsPopup/components/ViewScheduleFilesPopup/ViewScheduleFilesPopup';

import {
  BtnFlexContainerStyled,
  ClearBtnIconStyled,
  ClearBtnWrapStyled,
  FilterChipsRowStyled,
  FlexContainerStyled,
  HeaderStyled,
  InputStyled,
  MobileContainerStyled,
  MobileHeaderRowStyled,
  SearchAndFiltersRowStyled,
  SearchBarWrapStyled,
} from './Header.styled';
import useHeader from './hooks/useHeader';

export type HeaderPropsT = {
  onSaveChanges: (data: Record<string, ChangeTicketsListFormValues>) => Promise<void>;
  formMethods: UseFormReturn<Record<string, ChangeTicketsListFormValues>>;
  isCardsRowExpanded?: boolean;
  onToggleCardsRow?: () => void;
};

const Header: FC<HeaderPropsT> = ({
  onSaveChanges,
  formMethods,
  isCardsRowExpanded = true,
  onToggleCardsRow,
}) => {
  const {
    onChangeInput,
    searchText,
    openNewTicketPopup,
    reassignTicketPopup,
    openCloseTicketPopup,
    shareTicketPopup,
    handleExportClick,
    handleExportScheduleClick,
    openImportSchedulePopup,
    isExportingTickets,
    isExportingScheduleTickets,
    resetSearchTerm,
    handleOpenViewScheduleFilesPopup,
    handleCloseViewScheduleFilesPopup,
  } = useHeader();
  const { isMobile } = useResponsive();
  const {
    activeTab,
    selectedRoofTop,
    selectedTicketsIds,
    isEditMode,
    setEditMode,
    isScheduleTicketList,
    isViewScheduleFilesPopupOpened,
  } = useTickets();
  const { setColumnPickerPopupVisibility } = useUserProfile();
  const { handleSubmit } = formMethods;

  const getEditTooltipTitle = (): string => {
    if (!selectedTicketsIds.length) return 'Please select tickets first';
    return isEditMode
      ? formatTooltipWithShortcut('Cancel', 'cancelEdit')
      : formatTooltipWithShortcut('Edit selected tickets', 'editSelectedTickets');
  };

  return (
    <HeaderStyled>
      <FlexContainerStyled>
        <MobileContainerStyled
          direction={isMobile ? 'column' : 'row'}
          align={isMobile ? 'start' : 'center'}
        >
          {isMobile && !isScheduleTicketList ? (
            <TicketsFiltersBar
              renderMobileLayout={({ filterButtons, filterChips }): JSX.Element => (
                <>
                  <MobileHeaderRowStyled>
                    <SearchAndFiltersRowStyled>
                      <SearchBarWrapStyled title="Search for RO, Tech, Advisor...">
                        <InputStyled
                          id="searchTerm"
                          name="searchTerm"
                          icon={<SearchIcon />}
                          placeholder="Search for RO, Tech, Advisor..."
                          value={searchText}
                          onChange={onChangeInput}
                          bg="grey"
                        />
                        {searchText && (
                          <ClearBtnWrapStyled justify="center">
                            <ClearBtnIconStyled
                              size="md"
                              variant="transparent"
                              icon={<CloseIcon size="xs" />}
                              onClick={resetSearchTerm}
                            />
                          </ClearBtnWrapStyled>
                        )}
                      </SearchBarWrapStyled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {filterButtons}
                      </Box>
                    </SearchAndFiltersRowStyled>
                    <BtnFlexContainerStyled justify="end">
                      {activeTab === ActiveTabTypeE.Schedule ? (
                        <>
                          <Tooltip
                            title={
                              !selectedTicketsIds.length
                                ? 'Please select tickets first'
                                : formatTooltipWithShortcut('Close', 'closeTickets')
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                aria-label="Close"
                                onClick={openCloseTicketPopup}
                                disabled={!selectedTicketsIds.length}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                <CancelPresentationIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={formatTooltipWithShortcut(
                              'View Imported Schedule List',
                              'viewScheduleFiles'
                            )}
                            arrow
                          >
                            <span>
                              <IconButton
                                aria-label="View imported schedule list"
                                onClick={handleOpenViewScheduleFilesPopup}
                                disabled={selectedRoofTop.length < 1}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                <CalendarMonthIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={formatTooltipWithShortcut('Export', 'exportTickets')}
                            arrow
                          >
                            <span>
                              <IconButton
                                aria-label="Export"
                                onClick={handleExportScheduleClick}
                                disabled={selectedRoofTop.length < 1 || isExportingScheduleTickets}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                <FileDownloadIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={formatTooltipWithShortcut('Import', 'importSchedule')}
                            arrow
                          >
                            <IconButton
                              aria-label="Import"
                              onClick={openImportSchedulePopup}
                              sx={{ color: (theme): string => theme.palette.primary.main }}
                            >
                              <FileUploadIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={formatTooltipWithShortcut(
                              'Customize table columns',
                              'openColumnPicker'
                            )}
                            arrow
                          >
                            <IconButton
                              aria-label="Customize columns"
                              onClick={(): void => setColumnPickerPopupVisibility(true)}
                              sx={{ color: (theme): string => theme.palette.primary.main }}
                            >
                              <ViewColumnIcon />
                            </IconButton>
                          </Tooltip>
                          {onToggleCardsRow != null && (
                            <Tooltip
                              title={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'}
                              arrow
                            >
                              <IconButton
                                aria-label={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'}
                                onClick={onToggleCardsRow}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                {isCardsRowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      ) : (
                        <>
                          <Tooltip title={getEditTooltipTitle()} arrow>
                            <span>
                              <IconButton
                                aria-label={isEditMode ? 'Cancel' : 'Edit'}
                                onClick={(): void => setEditMode(!isEditMode)}
                                disabled={!selectedTicketsIds.length}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                {isEditMode ? <EditOffIcon /> : <EditIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {isEditMode && selectedTicketsIds.length > 0 ? (
                            <Tooltip title={formatTooltipWithShortcut('Save', 'save')} arrow>
                              <IconButton
                                aria-label="Save"
                                onClick={handleSubmit(onSaveChanges)}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip
                            title={
                              !selectedTicketsIds.length
                                ? 'Please select tickets first'
                                : formatTooltipWithShortcut('Close', 'closeTickets')
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                aria-label="Close"
                                onClick={openCloseTicketPopup}
                                disabled={!selectedTicketsIds.length}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                <CancelPresentationIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              !selectedTicketsIds.length
                                ? 'Please select tickets first'
                                : formatTooltipWithShortcut('Share', 'share')
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                aria-label="Share"
                                onClick={shareTicketPopup}
                                disabled={!selectedTicketsIds.length}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                <ShareIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              !selectedTicketsIds.length
                                ? 'Please select tickets first'
                                : formatTooltipWithShortcut('Reassign', 'reassign')
                            }
                            arrow
                          >
                            <span>
                              <IconButton
                                aria-label="Reassign"
                                onClick={reassignTicketPopup}
                                disabled={!selectedTicketsIds.length}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                <SocialDistanceIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={formatTooltipWithShortcut('Export', 'exportTickets')}
                            arrow
                          >
                            <span>
                              <IconButton
                                aria-label="Export"
                                onClick={handleExportClick}
                                disabled={isExportingTickets}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                <FileDownloadIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={formatTooltipWithShortcut(
                              'Customize table columns',
                              'openColumnPicker'
                            )}
                            arrow
                          >
                            <IconButton
                              aria-label="Customize columns"
                              onClick={(): void => setColumnPickerPopupVisibility(true)}
                              sx={{ color: (theme): string => theme.palette.primary.main }}
                            >
                              <ViewColumnIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={formatTooltipWithShortcut('New ticket', 'newTicket')}
                            arrow
                          >
                            <IconButton
                              aria-label="New Ticket"
                              onClick={openNewTicketPopup}
                              className="open-ticket-btn"
                              sx={{ color: (theme): string => theme.palette.primary.main }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                          {onToggleCardsRow != null && (
                            <Tooltip
                              title={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'}
                              arrow
                            >
                              <IconButton
                                aria-label={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'}
                                onClick={onToggleCardsRow}
                                sx={{ color: (theme): string => theme.palette.primary.main }}
                              >
                                {isCardsRowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </BtnFlexContainerStyled>
                  </MobileHeaderRowStyled>
                  <FilterChipsRowStyled>{filterChips}</FilterChipsRowStyled>
                </>
              )}
            />
          ) : (
            <>
              <SearchAndFiltersRowStyled>
                <SearchBarWrapStyled title="Search for RO, Tech, Advisor...">
                  <InputStyled
                    id="searchTerm"
                    name="searchTerm"
                    icon={<SearchIcon />}
                    placeholder="Search for RO, Tech, Advisor..."
                    value={searchText}
                    onChange={onChangeInput}
                    bg="grey"
                  />
                  {searchText && (
                    <ClearBtnWrapStyled justify="center">
                      <ClearBtnIconStyled
                        size="md"
                        variant="transparent"
                        icon={<CloseIcon size="xs" />}
                        onClick={resetSearchTerm}
                      />
                    </ClearBtnWrapStyled>
                  )}
                </SearchBarWrapStyled>
                <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                  <TicketsFiltersBar />
                </Box>
              </SearchAndFiltersRowStyled>
            </>
          )}
        </MobileContainerStyled>
        {!isScheduleTicketList && !isMobile && (
          <BtnFlexContainerStyled>
            {/* Temp hidden WARR-8794 */}
            {/* {activeTab === ActiveTabTypeE.Schedule && (
              <Tooltip
                title={
                  !isScheduleAutomationEnabled
                    ? 'Automation Disabled'
                    : hasRequestedGetDmsScheduleToday
                    ? 'Schedule Request Limit Reached For This Rooftop'
                    : null
                }
                arrow
              >
                <Box>
                  <ButtonStyled
                    size="md"
                    variant="outline"
                    onClick={handleCreateDmsScheduleIntegrationRequest}
                    disabled={
                      !isScheduleAutomationEnabled ||
                      hasRequestedGetDmsScheduleToday ||
                      selectedRoofTop.length < 1 ||
                      isScheduleAutomationStatusLoading
                    }
                  >
                    Request Schedule
                  </ButtonStyled>
                </Box>
              </Tooltip>
            )} */}
            <Tooltip title={getEditTooltipTitle()} arrow>
              <span>
                <IconButton
                  aria-label={isEditMode ? 'Cancel' : 'Edit'}
                  onClick={(): void => setEditMode(!isEditMode)}
                  disabled={!selectedTicketsIds.length}
                  sx={{ color: (theme): string => theme.palette.primary.main }}
                >
                  {isEditMode ? <EditOffIcon /> : <EditIcon />}
                </IconButton>
              </span>
            </Tooltip>

            {isEditMode && selectedTicketsIds.length > 0 ? (
              <Tooltip title={formatTooltipWithShortcut('Save', 'save')} arrow>
                <IconButton
                  aria-label="Save"
                  onClick={handleSubmit(onSaveChanges)}
                  sx={{ color: (theme): string => theme.palette.primary.main }}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
            ) : null}
            {activeTab === ActiveTabTypeE.Schedule ? (
              <>
                <Tooltip
                  title={
                    !selectedTicketsIds.length
                      ? 'Please select tickets first'
                      : formatTooltipWithShortcut('Close', 'closeTickets')
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      aria-label="Close"
                      onClick={openCloseTicketPopup}
                      disabled={!selectedTicketsIds.length}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      <CancelPresentationIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={formatTooltipWithShortcut(
                    'View Imported Schedule List',
                    'viewScheduleFiles'
                  )}
                  arrow
                >
                  <span>
                    <IconButton
                      aria-label="View imported schedule list"
                      onClick={handleOpenViewScheduleFilesPopup}
                      disabled={selectedRoofTop.length < 1}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      <CalendarMonthIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={formatTooltipWithShortcut('Export', 'exportTickets')} arrow>
                  <span>
                    <IconButton
                      aria-label="Export"
                      onClick={handleExportScheduleClick}
                      disabled={selectedRoofTop.length < 1 || isExportingScheduleTickets}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={formatTooltipWithShortcut('Import', 'importSchedule')} arrow>
                  <IconButton
                    aria-label="Import"
                    onClick={openImportSchedulePopup}
                    sx={{ color: (theme): string => theme.palette.primary.main }}
                  >
                    <FileUploadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={formatTooltipWithShortcut('Customize table columns', 'openColumnPicker')}
                  arrow
                >
                  <IconButton
                    aria-label="Customize columns"
                    onClick={(): void => setColumnPickerPopupVisibility(true)}
                    sx={{ color: (theme): string => theme.palette.primary.main }}
                  >
                    <ViewColumnIcon />
                  </IconButton>
                </Tooltip>
                {onToggleCardsRow != null && (
                  <Tooltip title={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'} arrow>
                    <IconButton
                      aria-label={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'}
                      onClick={onToggleCardsRow}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      {isCardsRowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : (
              <>
                <Tooltip
                  title={
                    !selectedTicketsIds.length
                      ? 'Please select tickets first'
                      : formatTooltipWithShortcut('Close', 'closeTickets')
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      aria-label="Close"
                      onClick={openCloseTicketPopup}
                      disabled={!selectedTicketsIds.length}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      <CancelPresentationIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    !selectedTicketsIds.length
                      ? 'Please select tickets first'
                      : formatTooltipWithShortcut('Share', 'share')
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      aria-label="Share"
                      onClick={shareTicketPopup}
                      disabled={!selectedTicketsIds.length}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      <ShareIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    !selectedTicketsIds.length
                      ? 'Please select tickets first'
                      : formatTooltipWithShortcut('Reassign', 'reassign')
                  }
                  arrow
                >
                  <span>
                    <IconButton
                      aria-label="Reassign"
                      onClick={reassignTicketPopup}
                      disabled={!selectedTicketsIds.length}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      <SocialDistanceIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={formatTooltipWithShortcut('Export', 'exportTickets')} arrow>
                  <span>
                    <IconButton
                      aria-label="Export"
                      onClick={handleExportClick}
                      disabled={isExportingTickets}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip
                  title={formatTooltipWithShortcut('Customize table columns', 'openColumnPicker')}
                  arrow
                >
                  <IconButton
                    aria-label="Customize columns"
                    onClick={(): void => setColumnPickerPopupVisibility(true)}
                    sx={{ color: (theme): string => theme.palette.primary.main }}
                  >
                    <ViewColumnIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={formatTooltipWithShortcut('New ticket', 'newTicket')} arrow>
                  <IconButton
                    aria-label="New Ticket"
                    onClick={openNewTicketPopup}
                    className="open-ticket-btn"
                    sx={{ color: (theme): string => theme.palette.primary.main }}
                  >
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                {onToggleCardsRow != null && (
                  <Tooltip title={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'} arrow>
                    <IconButton
                      aria-label={isCardsRowExpanded ? 'Collapse cards' : 'Expand cards'}
                      onClick={onToggleCardsRow}
                      sx={{ color: (theme): string => theme.palette.primary.main }}
                    >
                      {isCardsRowExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </BtnFlexContainerStyled>
        )}
      </FlexContainerStyled>
      <Divider />
      <ViewScheduleFilesPopup
        rooftopId={selectedRoofTop}
        isOpen={isViewScheduleFilesPopupOpened}
        onClose={handleCloseViewScheduleFilesPopup}
      />
    </HeaderStyled>
  );
};

export default Header;
